import { uniqBy as _uniqBy } from 'lodash-es'
import { Model, Factory, belongsTo, hasMany, trait } from 'miragejs'
import type { ModelInstance } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randSentence,
  randPastDate,
  randRecentDate,
  randNumber,
  randFirstName,
  randLastName,
  randPhoneNumber,
  randChanceBoolean,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../../types.js'
import type { MockServer, MirageServer } from '../server.js'
import type { UserAttributes } from '@/models/users.js'
import {
  sortable,
  pageable,
  filterable,
  createErrorResponse,
} from '../utils.js'
import {
  ConversationStatus,
  ConversationPriority,
  MessageReceiptStatus,
  MessageDirection,
} from '@/models/conversations/conversations.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { getInitials } from '@/utils/string.js'
import { subtractToDate } from '@/utils/date/calculation.js'
import { CONSTANTS } from './constants.js'
import type { ConversationMessageMediaData } from './ConversationMessageMedia.js'
import type { MessageReceiptData } from './MessageReceipt.js'
import {
  handleUpdatePriority,
  handleUpdateStatus,
  handleAssignEmployee,
  handleAssignPatient,
} from './handlers/update-thread.js'

export type ConversationData = SchemaAttributes<'ConversationThreadList'>
export type MessageData = SchemaAttributes<'ConversationMessage'>

export type ParticipantData = SchemaAttributes<'ConversationParticipant'>
export type ParticipantInstance = ModelInstance<ParticipantData>
export type ConversationInstance = ModelInstance<ConversationData> & {
  readonly assignedToUser: ModelInstance<UserAttributes> | null
  readonly patient: ModelInstance | null
  readonly participants: { models: ParticipantInstance[] }
  readonly messages: { models: MessageInstance[] }
}
export type MessageInstance = ModelInstance<MessageData> & {
  sender: ParticipantInstance
  messageReceipts: { models: ModelInstance<MessageReceiptData>[] }
  conversationMessageMedia: {
    models: ModelInstance<ConversationMessageMediaData>[]
  }
}

export const model = (Model as ModelDefinition<ConversationData>).extend({
  department: belongsTo('department'),
  assignedToUser: belongsTo('user'),
  patient: belongsTo('patient'),
  participants: hasMany('participant'),
  messages: hasMany('message'),
})

/**
 * Creates a default client participant for a conversation from a patient's client
 */
function createDefaultParticipant(
  server: MockServer,
  conversation: ConversationInstance,
) {
  // Create a patient (which auto-creates a client via Patient factory)
  const patient = server.create('patient', 'withBreed')
  const client = patient.client

  // Some clients get extra patients so not all have exactly one
  // Deterministic distribution per conversation id (0..2) to avoid test flakiness
  const baseId = Number(conversation.id) || 0
  const extraPatients = baseId % 3
  for (let i = 0; i < extraPatients; i++) {
    server.create('patient', 'withBreed', { client })
  }

  // Get client name
  const isOrganisation = client.customer_type !== 'CUSTOMER_TYPE_PRIVATE'
  const clientName = isOrganisation
    ? client.organization_name || ''
    : `${client.first_name || ''} ${client.last_name || ''}`.trim()

  const nameParts = clientName.split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''
  const initials = getInitials([firstName, lastName])

  const clientPhoneNumber = randPhoneNumber({
    countryCode: CONSTANTS.participant.defaultCountryCode,
  })

  const clientParticipant = server.create('participant', {
    conversation,
    participant_type: 'client',
    participant_internal_id: Number(client.id),
    created_at: conversation.created_at,
    updated_at: conversation.updated_at,
    info: {
      first_name: firstName,
      last_name: lastName,
      initials,
      phone_number: clientPhoneNumber,
      avatar: '',
    },
  })

  return clientParticipant
}

/**
 * Generates messages for a conversation with sequential timestamps
 */
function generateConversationMessages(
  server: MockServer,
  conversation: ConversationInstance,
  participants: ParticipantInstance[],
) {
  // Create random messages for the conversation (5-15 messages)
  const messageCount = randNumber(CONSTANTS.message.countPerConversation)

  // Generate a base date for the conversation (recent, within last 7 days)
  const today = getTodayDateTimeISO()
  const daysAgo = randNumber({
    min: 0,
    max: CONSTANTS.conversation.dateRange.recentDays,
  })
  const baseDate = subtractToDate(today, { days: daysAgo })

  // Create messages with sequential timestamps in descending order (newest first)
  let currentDate = baseDate
  for (let i = 0; i < messageCount; i++) {
    const message = server.create('message', {
      conversation,
      sender: rand(participants),
      direction: rand([...CONSTANTS.message.directions]),
      message_type: CONSTANTS.message.defaultType,
      created_at: currentDate.toISOString(),
      updated_at: currentDate.toISOString(),
    })

    if (randChanceBoolean({ chanceTrue: 0.2 })) {
      attachMediaToMessage(server, message)
    }

    // Subtract random minutes for the next (older) message
    const minutesOffset = randNumber(CONSTANTS.message.timeOffsetMinutes)
    currentDate = subtractToDate(currentDate, { minutes: minutesOffset })
  }

  return messageCount
}

/**
 * Attaches random media to a message and updates its message_type
 */
function attachMediaToMessage(server: MockServer, message: ModelInstance) {
  const mediaCount = randNumber(CONSTANTS.media.countPerMessage)
  const mediaTypes = new Set<string>()

  for (let j = 0; j < mediaCount; j++) {
    const media = server.create('conversationMessageMedia', { message })
    mediaTypes.add(media.media_type)
  }

  const messageType =
    mediaTypes.size > 1
      ? 'mixed'
      : mediaTypes.has('image')
        ? 'image'
        : 'document'
  message.update({ message_type: messageType })
}

/**
 * Serializes participants to JSON:API format
 */
function serializeParticipants(participants: ParticipantInstance[]) {
  return participants.map((participant: ParticipantInstance) => ({
    type: 'ConversationParticipant',
    id: participant.id,
    attributes: {
      participant_type: participant.attrs.participant_type,
      participant_internal_id: participant.attrs.participant_internal_id,
      created_at: participant.attrs.created_at,
      updated_at: participant.attrs.updated_at,
      info: participant.attrs.info,
    },
  }))
}

/**
 * Serializes a user to JSON:API format for includes
 */
function serializeUser(user: ConversationInstance['assignedToUser']) {
  if (!user) {
    return null
  }
  return {
    type: 'User',
    id: user.id,
    attributes: {
      first_name: user.attrs.first_name,
      last_name: user.attrs.last_name,
      initials: user.attrs.initials,
      email: user.attrs.email,
    },
  }
}

/**
 * Sorts messages by created_at timestamp (oldest first, newest last)
 */
function sortMessagesByDate(messages: MessageInstance[]) {
  return [...messages].sort((a: MessageInstance, b: MessageInstance) => {
    const dateA = a.attrs.created_at || ''
    const dateB = b.attrs.created_at || ''
    return dateA.localeCompare(dateB)
  })
}

/**
 * Checks if any outbound message in a conversation has a failed receipt
 */
function hasFailedReceipts(
  server: MockServer,
  conversation: ConversationInstance,
): boolean {
  const messages = conversation.messages?.models || []
  for (const message of messages) {
    // Only check outbound messages (messages sent to clients)
    if (message.attrs.direction !== MessageDirection.Outbound) {
      continue
    }

    const receipts = message.messageReceipts?.models || []
    const hasFailed = receipts.some(
      (receipt) => receipt.attrs.status === MessageReceiptStatus.Failed,
    )
    if (hasFailed) {
      return true
    }
  }
  return false
}

/**
 * Type for a serialized message in JSON:API format
 */
interface SerializedMessageResource {
  type: string
  id: string | undefined
  attributes: MessageInstance['attrs']
  relationships: {
    sender: {
      data: {
        type: string
        id: string | undefined
      } | null
    }
    message_receipts?: {
      data: Array<{ type: string; id: string }>
    }
    media?: {
      data: Array<{ type: string; id: string }>
    }
  }
}

/**
 * Type for a serialized media resource in JSON:API format
 */
interface SerializedMediaResource {
  type: 'ConversationMessageMedia'
  id: string | undefined
  attributes: ConversationMessageMediaData
}

/**
 * Type for JSON:API included resources in conversation responses
 */
type IncludedResource =
  | ReturnType<typeof serializeParticipants>[number]
  | NonNullable<ReturnType<typeof serializeUser>>
  | SerializedMessageResource
  | SerializedMediaResource
  | {
      type: string
      id: string | undefined
      attributes: {
        status: MessageReceiptData['status']
        error_code: MessageReceiptData['error_code']
        created_at: MessageReceiptData['created_at']
        updated_at: MessageReceiptData['updated_at']
      }
      relationships: {
        participant: {
          data: {
            type: string
            id: string | undefined
          }
        }
      }
    }

export const factory = Factory.extend<ConversationData>({
  topic() {
    return randSentence()
  },
  summary() {
    return rand([randSentence(), undefined])
  },
  status() {
    return rand(Object.values(ConversationStatus))
  },
  priority() {
    return rand(Object.values(ConversationPriority))
  },
  no_of_messages() {
    return randNumber(CONSTANTS.conversation.messagesCount)
  },
  unread_messages_count() {
    return randNumber(CONSTANTS.conversation.unreadMessagesCount)
  },
  tags() {
    return rand([randSentence(), undefined])
  },
  created_at() {
    return randPastDate({
      years: CONSTANTS.conversation.dateRange.years,
    }).toISOString()
  },
  updated_at() {
    return randRecentDate().toISOString()
  },
  last_message_at() {
    return randRecentDate().toISOString()
  },
  has_failed_messages() {
    return false
  },
  afterCreate(conversation, server) {
    if (!conversation.department) {
      const departments = server.schema.all('department').models
      if (departments.length > 0) {
        conversation.update({ department: rand(departments) })
      }
    }

    // Check if using traits that will handle participant creation
    const conversationAttrs = conversation.attrs as ConversationData & {
      clientId?: string
      isUnknownSender?: boolean
    }
    const usingClientParticipantTrait = !!conversationAttrs.clientId
    const usingUnknownSenderTrait = !!conversationAttrs.isUnknownSender

    // Only auto-create participants and messages if they don't already exist
    const hasParticipants =
      conversation.participants && conversation.participants.models.length > 0
    const hasMessages =
      conversation.messages && conversation.messages.models.length > 0

    if (
      !hasParticipants &&
      !usingClientParticipantTrait &&
      !usingUnknownSenderTrait
    ) {
      // Create participants for the conversation
      // Always create at least one client participant
      const clientParticipant = createDefaultParticipant(server, conversation)

      // Create additional random participants (0-2)
      const additionalParticipants = randNumber(
        CONSTANTS.participant.additionalParticipantsCount,
      )
      const allParticipants = [clientParticipant]
      for (let i = 0; i < additionalParticipants; i++) {
        const participantFirstName = randFirstName()
        const participantLastName = randLastName()
        const participantInitials = getInitials([
          participantFirstName,
          participantLastName,
        ])
        const participantPhoneNumber = randPhoneNumber({
          countryCode: CONSTANTS.participant.defaultCountryCode,
        })

        const participant = server.create('participant', {
          conversation,
          participant_type: rand([...CONSTANTS.participant.types]),
          participant_internal_id: randNumber(
            CONSTANTS.participant.internalIdRange,
          ),
          created_at: conversation.created_at,
          updated_at: conversation.updated_at,
          info: {
            first_name: participantFirstName,
            last_name: participantLastName,
            initials: participantInitials,
            phone_number: participantPhoneNumber,
            avatar: '',
          },
        })
        allParticipants.push(participant)
      }
    }

    if (!hasMessages) {
      // Get all participants for message creation
      const allParticipants = conversation.participants?.models || []

      if (allParticipants.length > 0) {
        const messageCount = generateConversationMessages(
          server,
          conversation,
          allParticipants,
        )
        conversation.update({
          no_of_messages: messageCount,
          has_failed_messages: hasFailedReceipts(server, conversation),
        })
      }
    }
  },
  withClientParticipant: trait({
    afterCreate(conversation, server) {
      // This trait expects a clientId in the attributes
      const clientId = (
        conversation.attrs as ConversationData & { clientId?: string }
      ).clientId
      const participantInfo = (
        conversation.attrs as ConversationData & {
          participantInfo?: {
            firstName: string
            lastName: string
            phoneNumber: string
            initials: string
          }
        }
      ).participantInfo

      if (clientId && participantInfo) {
        server.create('participant', {
          conversation,
          participant_type: 'client',
          participant_internal_id: parseInt(clientId, 10),
          created_at: conversation.created_at,
          updated_at: conversation.updated_at,
          info: {
            first_name: participantInfo.firstName,
            last_name: participantInfo.lastName,
            phone_number: participantInfo.phoneNumber,
            initials: participantInfo.initials,
            avatar: '',
          },
        })
      }
    },
  }),
  withUnknownSender: trait({
    // Flag to prevent default participant creation in afterCreate
    isUnknownSender: true,
    afterCreate(conversation, server) {
      // Create a participant with null internal_id (unknown sender)
      const phoneNumber = randPhoneNumber({
        countryCode: CONSTANTS.participant.defaultCountryCode,
      })

      const unknownParticipant = server.create('participant', {
        conversation,
        participant_type: 'client',
        participant_internal_id: null,
        created_at: conversation.created_at,
        updated_at: conversation.updated_at,
        info: {
          first_name: '',
          last_name: '',
          initials: '?',
          phone_number: phoneNumber,
          avatar: '',
        },
      }) as ParticipantInstance

      // Generate messages for this conversation
      generateConversationMessages(server, conversation, [unknownParticipant])

      // Update has_failed_messages based on receipt status
      conversation.update({
        has_failed_messages: hasFailedReceipts(server, conversation),
      })
    },
  }),
  withAssignedUser: trait({
    afterCreate(conversation, server) {
      if (!conversation.assignedToUser) {
        const users = server.schema.all('user').models
        if (users.length > 0) {
          conversation.update({ assignedToUser: rand(users) })
        }
      }
    },
  }),
  // withPatient must come after participant-creating traits/factory hooks
  // because it looks up the client via existing participants.
  withPatient: trait({
    afterCreate(conversation, server) {
      if (!conversation.patient) {
        const participants = conversation.participants?.models || []
        const clientParticipant = participants.find(
          (p: ParticipantInstance) => p.participant_type === 'client',
        )
        if (clientParticipant) {
          const client = server.schema.find(
            'client',
            String(clientParticipant.participant_internal_id),
          ) as
            | (ModelInstance & { patients?: { models: ModelInstance[] } })
            | null
          const patients = client?.patients?.models || []
          if (patients.length > 0) {
            // Mirage's update() types don't accept relationship objects
            conversation.update({ patient: patients[0] } as never)
          }
        }
      }
    },
  }),
  withMedia: trait({
    afterCreate(conversation, server) {
      const messages = conversation.messages?.models || []
      for (const message of messages) {
        attachMediaToMessage(server, message)
      }
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('conversation')

  server.get(
    '/conversations/threads',
    function (this: MirageServer, schema, request) {
      const conversations = schema.all('conversation')

      // Apply filtering first (before sorting/pagination)
      const filtered = filterable(conversations, request, {
        // Search filter - searches topic, summary, and tags
        search: (conversation, value) => {
          const searchTerm = value.toLowerCase()
          const topic = conversation.topic?.toLowerCase() || ''
          const summary = conversation.summary?.toLowerCase() || ''
          const tags = conversation.tags?.toLowerCase() || ''

          return (
            topic.includes(searchTerm) ||
            summary.includes(searchTerm) ||
            tags.includes(searchTerm)
          )
        },

        // Status filter - exact match
        status: (conversation, value) => {
          return conversation.status === value
        },

        // Priority filter - exact match
        priority: (conversation, value) => {
          return conversation.priority === value
        },

        // Assigned user filter - exact match
        assigned_to_user: (conversation, value) => {
          return conversation.assignedToUser?.id === value
        },

        // Client filter - exact match
        participant_client: (conversation, value) => {
          const participants = conversation.participants?.models || []
          const clientParticipant = participants.find(
            (p: ParticipantInstance) => p.participant_type === 'client',
          )
          return (
            clientParticipant?.attrs.participant_internal_id === Number(value)
          )
        },
      })

      // Apply sorting
      const sorted = sortable(
        filtered,
        request,
        {
          // ISO strings sort correctly without conversion
          last_message_at: (item) => item.last_message_at,
        },
        ['-last_message_at'],
      )

      // Apply pagination
      const { data, meta } = pageable(sorted, request)

      // Handle includes parameter
      const includes = request.queryParams.include as
        | undefined
        | string
        | string[]
      const includesArray = includes
        ? Array.isArray(includes)
          ? includes
          : includes.split(',').map((i) => i.trim())
        : []

      const serialized = this.serialize(data)

      // Build included array
      const included: IncludedResource[] = []

      // If participants are requested, ensure they're included
      if (includesArray.includes('participants')) {
        const allParticipants = data.models.flatMap((conversation) => {
          return conversation.participants?.models || []
        })

        if (allParticipants.length > 0) {
          const participantsData = serializeParticipants(allParticipants)
          included.push(...participantsData)
        }
      }

      // If assigned_to_user is requested, include the users
      if (includesArray.includes('assigned_to_user')) {
        const users = data.models
          .map((conversation) => serializeUser(conversation.assignedToUser))
          .filter(
            (u): u is NonNullable<ReturnType<typeof serializeUser>> => !!u,
          )
        // Deduplicate users by id
        const uniqueUsers = _uniqBy(users, 'id')
        included.push(...uniqueUsers)
      }

      // Return response with pagination meta
      return {
        ...serialized,
        ...(included.length > 0 && { included }),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.get(
    '/conversations/threads/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const conversation = schema.find('conversation', id)

      if (!conversation) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Conversation thread with id ${id} not found` }],
        })
      }

      // Handle includes parameter
      const includes = request.queryParams.include as
        | undefined
        | string
        | string[]
      const includesArray = includes
        ? Array.isArray(includes)
          ? includes
          : includes.split(',').map((i) => i.trim())
        : []

      const included: IncludedResource[] = []

      // If participants are requested, ensure they're included
      if (includesArray.includes('participants')) {
        const participants = conversation.participants?.models || []

        if (participants.length > 0) {
          const participantsData = serializeParticipants(participants)
          included.push(...participantsData)
        }
      }

      // If assigned_to_user is requested, include the user
      if (includesArray.includes('assigned_to_user')) {
        const user = serializeUser(conversation.assignedToUser)
        if (user) {
          included.push(user)
        }
      }

      // Get messages and sort by created_at (oldest first, newest last)
      const messages = conversation.messages?.models || []
      const sortedMessages = sortMessagesByDate(messages)

      // If messages are requested, ensure they're included
      if (includesArray.includes('messages') && sortedMessages.length > 0) {
        const includeReceipts = includesArray.includes(
          'messages.message_receipts',
        )
        const includeMedia = includesArray.includes('messages.media')

        const messagesData = sortedMessages.map((message: MessageInstance) => {
          const messageData: SerializedMessageResource = {
            type: 'ConversationMessage',
            id: message.id,
            attributes: message.attrs,
            relationships: {
              sender: {
                data: message.sender
                  ? {
                      type: 'ConversationParticipant',
                      id: message.sender.id,
                    }
                  : null,
              },
            },
          }

          if (includeReceipts) {
            const receipts = schema.where('messageReceipt', {
              messageId: message.id,
            })
            messageData.relationships.message_receipts = {
              data: receipts.models.map((receipt) => ({
                type: 'ConversationMessageReceipt',
                id: receipt.id as string,
              })),
            }
          }

          if (includeMedia) {
            const mediaModels = message.conversationMessageMedia?.models ?? []
            messageData.relationships.media = {
              data: mediaModels.map((media) => ({
                type: 'ConversationMessageMedia',
                id: media.id as string,
              })),
            }
          }

          return messageData
        })

        included.push(...messagesData)
      }

      if (
        includesArray.includes('messages.message_receipts') &&
        sortedMessages.length > 0
      ) {
        sortedMessages.forEach((message: MessageInstance) => {
          const receipts = schema.where('messageReceipt', {
            messageId: message.id,
          })

          for (const receipt of receipts.models) {
            const attrs = receipt.attrs as MessageReceiptData & {
              participantId?: string
            }
            const receiptData = {
              type: 'ConversationMessageReceipt',
              id: receipt.id,
              attributes: {
                status: attrs.status,
                error_code: attrs.error_code,
                created_at: attrs.created_at,
                updated_at: attrs.updated_at,
              },
              relationships: {
                participant: {
                  data: {
                    type: 'ConversationParticipant',
                    id: attrs.participantId,
                  },
                },
              },
            }

            included.push(receiptData)
          }
        })
      }

      // If media is requested, serialize ConversationMessageMedia resources
      if (
        includesArray.includes('messages.media') &&
        sortedMessages.length > 0
      ) {
        for (const message of sortedMessages) {
          const mediaModels = message.conversationMessageMedia?.models ?? []
          for (const media of mediaModels) {
            const attrs = media.attrs as ConversationMessageMediaData
            const mediaResource: SerializedMediaResource = {
              type: 'ConversationMessageMedia',
              id: media.id,
              attributes: {
                storage_type: attrs.storage_type,
                is_file_available: attrs.is_file_available,
                url: attrs.url,
                file_name: attrs.file_name,
                file_size: attrs.file_size,
                mime_type: attrs.mime_type,
                media_type: attrs.media_type,
                added_at: attrs.added_at,
                duration: attrs.duration,
                width: attrs.width,
                height: attrs.height,
              },
            }
            included.push(mediaResource)
          }
        }
      }

      // Serialize the conversation using schema.where to get a Collection
      const conversationCollection = schema.where('conversation', { id })
      const serialized = this.serialize(conversationCollection) as {
        data?: Array<{
          relationships?: {
            messages?: {
              data?: Array<{ type: string; id: string }>
            }
          }
        }>
      }

      // Extract the single conversation from the array
      const conversationData = serialized.data?.[0]

      // Override the relationships.messages.data to use sorted order
      if (
        sortedMessages.length > 0 &&
        conversationData?.relationships?.messages
      ) {
        conversationData.relationships.messages.data = sortedMessages
          .filter((message) => message.id !== undefined)
          .map((message: MessageInstance) => ({
            type: 'ConversationMessage',
            id: message.id as string,
          }))
      }

      if (included.length > 0) {
        return {
          data: conversationData,
          included,
        }
      }

      return {
        data: conversationData,
      }
    },
  )

  // Priority update endpoint
  server.post('/conversations/threads/:id/priority', handleUpdatePriority)

  // Status update endpoint
  server.post('/conversations/threads/:id/status', handleUpdateStatus)

  // Employee assignment endpoint
  server.post('/conversations/threads/:id/employee', handleAssignEmployee)

  // Patient assignment endpoint
  server.post('/conversations/threads/:id/patient', handleAssignPatient)
}

export async function seeds(server: MockServer) {
  // Ensure dependencies exist before creating conversations
  await server.lazySeedingManager?.seedBasic('Department')
  await server.lazySeedingManager?.seedBasic('User')
  await server.lazySeedingManager?.seedBasic('Client')

  // Create conversations with assigned users and patients
  server.createList(
    'conversation',
    CONSTANTS.conversation.assignedUserSeedCount,
    'withAssignedUser',
    'withPatient',
  )

  // Create conversations with patients only (no assigned user)
  const patientOnlyCount =
    CONSTANTS.conversation.patientSeedCount -
    CONSTANTS.conversation.assignedUserSeedCount
  server.createList('conversation', patientOnlyCount, 'withPatient')

  // Create conversations without assigned users or patients
  const unassignedCount =
    CONSTANTS.conversation.seedCount - CONSTANTS.conversation.patientSeedCount
  server.createList('conversation', unassignedCount)

  // Create conversations with unknown senders (also with assigned users)
  server.createList(
    'conversation',
    CONSTANTS.conversation.unknownSenderSeedCount,
    'withUnknownSender',
    'withAssignedUser',
  )
}
